import { ShieldCheck, FileText, AlertTriangle, Phone } from "lucide-react";

export default function Terms() {
  return (
    <section
      id="terms"
      data-testid="terms-section"
      className="relative py-24 md:py-28 px-6 md:px-10 bg-slate-50"
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-emerald-700">
          <FileText size={14} />
          Warranty terms · The short version
        </div>
        <h2 className="mt-4 font-display text-4xl md:text-5xl font-light tracking-tight">
          Your <span className="italic font-semibold">10‑year</span> warranty,
          plain and simple.
        </h2>
        <p className="mt-5 text-slate-600 leading-relaxed max-w-2xl">
          We keep this in plain English so there are no surprises. The full
          warranty document is provided with your invoice on the day of
          treatment.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* What's covered */}
          <div className="rounded-3xl bg-white border border-emerald-100 p-7 hover-lift">
            <div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <ShieldCheck size={20} />
            </div>
            <div className="mt-5 font-display text-2xl font-semibold text-emerald-950">
              What we guarantee
            </div>
            <ul className="mt-4 space-y-3 text-slate-600 text-sm leading-relaxed">
              <li className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0" />
                The ClearShield coating will remain hydrophobic and protective
                for <strong>10 years</strong> from the date of application.
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0" />
                If the coating fails under normal domestic use, we will
                re‑treat the affected glass free of charge.
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0" />
                Workmanship of our application is guaranteed for the full term.
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-600 flex-shrink-0" />
                Warranty is transferable to a new owner if you sell the property.
              </li>
            </ul>
          </div>

          {/* What voids it */}
          <div className="rounded-3xl bg-white border border-amber-200 p-7 hover-lift">
            <div className="w-11 h-11 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center">
              <AlertTriangle size={20} />
            </div>
            <div className="mt-5 font-display text-2xl font-semibold text-emerald-950">
              What invalidates it
            </div>
            <ul className="mt-4 space-y-3 text-slate-600 text-sm leading-relaxed">
              <li className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0" />
                Use of abrasive sponges, scouring pads or steel wool.
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0" />
                Use of bleach, vinegar, ammonia or any acidic/alkaline cleaner.
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0" />
                Use of any cleaner not listed in the approved Ritec aftercare range.
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0" />
                Physical damage, impact, scratching or third‑party re‑coating.
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0" />
                Loss of original invoice / proof of treatment.
              </li>
            </ul>
          </div>
        </div>

        {/* Fine print */}
        <div className="mt-10 rounded-3xl bg-white border border-slate-200 p-7 md:p-10">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">
            The fine print
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 text-sm text-slate-600 leading-relaxed">
            <p>
              <strong className="text-emerald-950">Scope.</strong> The warranty
              covers the ClearShield coating only and does not extend to the
              underlying glass, seals, frames or fittings.
            </p>
            <p>
              <strong className="text-emerald-950">Claims.</strong> Notify us in
              writing (email or post) within 30 days of identifying an issue.
              We will arrange an inspection at a mutually convenient time.
            </p>
            <p>
              <strong className="text-emerald-950">Remedy.</strong> Our
              liability is limited to re‑treating the affected glass. We do
              not cover indirect or consequential losses.
            </p>
            <p>
              <strong className="text-emerald-950">Aftercare.</strong> Customer
              must follow the maintenance schedule using approved Ritec
              aftercare products only. A maintenance package with J7 Collective
              also satisfies this requirement.
            </p>
            <p>
              <strong className="text-emerald-950">Records.</strong> Keep your
              invoice safe — it is your proof of treatment and the start date
              of the warranty.
            </p>
            <p>
              <strong className="text-emerald-950">Governing law.</strong>
              {" "}This warranty is governed by the laws of England &amp; Wales.
            </p>
          </div>

          <div className="mt-7 pt-6 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
            <div className="flex items-center gap-2 text-slate-500">
              <Phone size={14} /> Got a question about your warranty? Call us
              on <a href="tel:+447497498669" className="font-semibold text-emerald-800 hover:text-emerald-600">07497 498669</a>.
            </div>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="text-sm font-semibold text-emerald-800 hover:text-emerald-600"
            >
              Get this in writing → request a copy
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
